'use client';

import { REVIEW_SOURCE_ICON_PATHS } from '@/utils/reviewSourceAlt';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import {
    getFacebookEmbedUrl,
    isFacebookVideoUrl,
    isVideoUrl,
} from '@/utils/isVideoUrl';
import {
    Cog6ToothIcon,
    CodeBracketIcon,
    PlayIcon,
    PhotoIcon,
    SparklesIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEditor } from './context/EditorContext/EditorContext';
import { useNotifications } from './context/NotificationContext/NotificationContext';
import { renameImageAsset } from './helpers/imageRename';
import {
    formatImageFileSize,
    getImageSizeBadgeState,
} from './helpers/imageSize';

const STOCK_IMAGES = [
    REVIEW_SOURCE_ICON_PATHS.google,
    REVIEW_SOURCE_ICON_PATHS.nicejob,
    REVIEW_SOURCE_ICON_PATHS.facebook,
];

/* ----------------------------- URL helpers ----------------------------- */

const isImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    return (
        /^data:image\//i.test(u) ||
        /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(u)
    );
};

const isSvgMarkup = (val) =>
    typeof val === 'string' && /<\s*svg[\s>]/i.test(val);

const svgMarkupToDataUrl = (markup) => {
    const value = String(markup || '').trim();
    if (!value) return '';
    try {
        const utf8 =
            typeof window !== 'undefined' && typeof window.btoa === 'function'
                ? window.btoa(unescape(encodeURIComponent(value)))
                : btoa(unescape(encodeURIComponent(value)));
        return `data:image/svg+xml;base64,${utf8}`;
    } catch {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
    }
};

/* Raw embed (iframe/script/video/div/etc.) detector */
const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

const getMediaType = (value) => {
    const val = String(value || '').trim();
    if (!val) return null;
    if (isSvgMarkup(val)) return 'image';
    if (isEmbedCode(val)) return 'embed';
    if (isVideoUrl(val)) return 'video';
    if (isImageUrl(val)) return 'image';
    return 'unknown';
};

const getImagePreviewSrc = (value) => {
    const val = String(value || '').trim();
    if (!val) return '';
    if (isSvgMarkup(val)) {
        return svgMarkupToDataUrl(val);
    }
    return val;
};

function SvgMarkupPreview({ markup, className = '' }) {
    return (
        <div
            className={className}
            dangerouslySetInnerHTML={{ __html: String(markup || '').trim() }}
        />
    );
}

const getMediaDisplayName = (value, type) => {
    const val = String(value || '').trim();
    if (!val) return 'Selected media';
    if (isSvgMarkup(val)) return 'SVG image';
    if (type === 'embed') return 'Custom embed';
    if (type === 'video') return 'External video';
    try {
        const normalized = val.startsWith('http') ? new URL(val).pathname : val;
        const fileName = normalized.split('/').pop() || '';
        return decodeURIComponent(fileName || val);
    } catch {
        return val;
    }
};

const normalizeMediaUrlForComparison = (value) => {
    const raw = String(value || '').trim();
    if (
        !raw ||
        isSvgMarkup(raw) ||
        isEmbedCode(raw) ||
        raw.startsWith('data:')
    ) {
        return raw;
    }

    try {
        const base =
            typeof window !== 'undefined'
                ? window.location.origin
                : 'https://media.local';
        const parsed = new URL(raw, base);
        let pathname = parsed.pathname;
        try {
            pathname = decodeURIComponent(pathname);
        } catch {
            // Keep the original pathname when it contains malformed escapes.
        }
        return `${parsed.origin.toLowerCase()}${pathname.replace(/\/+$/, '')}`;
    } catch {
        return raw.replace(/[?#].*$/, '').replace(/\/+$/, '');
    }
};

const mediaUrlsMatch = (left, right) => {
    const leftRaw = String(left || '').trim();
    const rightRaw = String(right || '').trim();
    if (!leftRaw || !rightRaw) return false;
    if (leftRaw === rightRaw) return true;
    return (
        normalizeMediaUrlForComparison(leftRaw) ===
        normalizeMediaUrlForComparison(rightRaw)
    );
};

/* -------------------------- Gradient Field UI -------------------------- */
function GradientField({ children, className = '' }) {
    return (
        <div
            className={[
                'rounded-xl bg-neutral-300 p-[2px]',
                'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600',
                className,
            ].join(' ')}
        >
            {children}
        </div>
    );
}

function ImageThumbnail({
    value,
    alt,
    className = '',
    svgClassName = '',
    draggable = false,
}) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return null;

    if (isSvgMarkup(normalizedValue)) {
        return (
            <SvgMarkupPreview
                markup={normalizedValue}
                className={svgClassName || className}
            />
        );
    }

    return (
        <img
            src={getImagePreviewSrc(normalizedValue)}
            alt={alt}
            className={className}
            draggable={draggable}
        />
    );
}

const formatImageFileType = (image) => {
    const filename = String(image?.filename || image?.url || '').trim();
    const ext = filename.split('.').pop()?.split('?')[0]?.split('#')[0] || '';
    return ext ? ext.toUpperCase() : 'Unknown';
};

const formatImageDimensions = (image, fallbackMeta) => {
    const width = Number(
        image?.width || image?.pixel_width || fallbackMeta?.width
    );
    const height = Number(
        image?.height || image?.pixel_height || fallbackMeta?.height
    );
    if (
        Number.isFinite(width) &&
        width > 0 &&
        Number.isFinite(height) &&
        height > 0
    ) {
        return `${Math.round(width)} x ${Math.round(height)} px`;
    }
    return 'Unknown';
};

const splitFilenameParts = (filename) => {
    const trimmed = String(filename || '').trim();
    if (!trimmed) return { name: '', extension: '' };
    const lastDot = trimmed.lastIndexOf('.');
    if (lastDot <= 0) return { name: trimmed, extension: '' };
    return {
        name: trimmed.slice(0, lastDot),
        extension: trimmed.slice(lastDot),
    };
};

const buildFilenameFromParts = (name, extension = '') => {
    const baseName = String(name || '')
        .trim()
        .replace(/[\\/]+/g, '');
    const normalizedExtension = String(extension || '').trim();
    if (!baseName) return '';
    return `${baseName}${normalizedExtension}`;
};

const readSiteImageAltText = (image) =>
    String(
        image?.alt_text ?? image?.altText ?? image?.alt ?? image?.AltText ?? ''
    );

/* ---------------------- Unified Media Panel ---------------------- */
export function MediaPanel({
    onSelectImage,
    onSelectVideo,
    onSelectCustom,
    onPreviewSelect,
    enableVideo = true,
    currentValue,
    showBackgroundVideoWarning = false,
    inline = false,
    hidePreview = false,
    inlineAltValue = '',
    onInlineAltChange = null,
    imageFilter = null,
    showStockImages = true,
    inputPlaceholder = 'Paste an image URL, video URL, or embed code...',
    inputHelperText = 'Image URL, video link, or HTML code',
}) {
    const editor = useEditor();
    const notifications = useNotifications();
    const [inputValue, setInputValue] = useState('');
    const [previewValue, setPreviewValue] = useState('');
    const [isExternalFocused, setIsExternalFocused] = useState(false);
    const [activeSettingsImageId, setActiveSettingsImageId] = useState(null);
    const [settingsNameDraft, setSettingsNameDraft] = useState('');
    const [settingsAltDraft, setSettingsAltDraft] = useState('');
    const [settingsImageMeta, setSettingsImageMeta] = useState(null);
    const [isSavingImageSettings, setIsSavingImageSettings] = useState(false);

    /* ---------- Prefill when opening based on currentValue ---------- */
    useEffect(() => {
        const initial = currentValue ? String(currentValue).trim() : '';
        setInputValue(initial);
        setPreviewValue(initial);
    }, [currentValue]);

    /* ---------- Live preview for typed links ---------- */
    useEffect(() => {
        const next = inputValue.trim();
        setPreviewValue(next);
    }, [inputValue]);

    /* ---------- Auto-Detect Type ---------- */
    const detectedType = useMemo(() => getMediaType(inputValue), [inputValue]);

    const previewType = useMemo(
        () => getMediaType(previewValue),
        [previewValue]
    );

    /* ---------- Actions ---------- */
    const applyMediaValue = useCallback(
        (value) => {
            const val = String(value || '').trim();
            const type = getMediaType(val);
            if (!val) {
                if (inline) {
                    onSelectImage?.('');
                    return;
                }
                onPreviewSelect?.('');
                return;
            }
            if (inline) {
                if (type === 'embed') {
                    onSelectCustom?.(val);
                    return;
                }
                if (type === 'video') {
                    if (enableVideo) onSelectVideo?.(val);
                    return;
                }
                onSelectImage?.(val);
                return;
            }
            onPreviewSelect?.(val);
        },
        [
            enableVideo,
            inline,
            onPreviewSelect,
            onSelectCustom,
            onSelectImage,
            onSelectVideo,
        ]
    );

    const handleApply = useCallback(() => {
        const val = inputValue.trim();
        if (!val) return;

        setPreviewValue(val);
        const type = getMediaType(val);

        if (inline) {
            applyMediaValue(val);
            return;
        }

        if (type === 'embed') {
            onSelectCustom?.(val);
            return;
        }

        if (type === 'video') {
            if (enableVideo) onSelectVideo?.(val);
            return;
        }

        onSelectImage?.(val);
    }, [
        applyMediaValue,
        enableVideo,
        inline,
        inputValue,
        onSelectCustom,
        onSelectImage,
        onSelectVideo,
    ]);

    const handleInputChange = useCallback(
        (nextRawValue) => {
            setInputValue(nextRawValue);

            const nextValue = String(nextRawValue || '').trim();
            setPreviewValue(nextValue);

            if (!inline) return;

            if (!nextValue) {
                applyMediaValue('');
                return;
            }

            applyMediaValue(nextValue);
        },
        [applyMediaValue, inline]
    );

    const handleExternalInputFocus = useCallback((event) => {
        setIsExternalFocused(true);
        event.currentTarget.select();
    }, []);

    const isValid = detectedType && detectedType !== 'unknown';
    const isVideoAndDisabled = detectedType === 'video' && !enableVideo;
    const selectedAssetValue = String(
        inline ? currentValue ?? '' : previewValue ?? ''
    ).trim();
    const selectedAssetType = getMediaType(selectedAssetValue);

    const images = editor.site?.data?.images;
    const isImagesLoading = images === undefined;
    const selectedLibraryImage = useMemo(
        () =>
            images?.find((image) =>
                mediaUrlsMatch(image?.url, selectedAssetValue)
            ) || null,
        [images, selectedAssetValue]
    );
    const filteredImages = useMemo(
        () =>
            Array.isArray(images) && typeof imageFilter === 'function'
                ? images.filter(imageFilter)
                : images,
        [imageFilter, images]
    );
    const orderedImages = useMemo(() => {
        if (!Array.isArray(filteredImages) || !selectedLibraryImage)
            return filteredImages;
        const selectedIndex = filteredImages.findIndex(
            (image) => image?.image_id === selectedLibraryImage.image_id
        );
        if (selectedIndex <= 0) return filteredImages;
        return [
            filteredImages[selectedIndex],
            ...filteredImages.slice(0, selectedIndex),
            ...filteredImages.slice(selectedIndex + 1),
        ];
    }, [filteredImages, selectedLibraryImage]);
    const activeSettingsImage =
        images?.find((image) => image.image_id === activeSettingsImageId) ||
        null;
    const hasSelectedLibraryImage = Boolean(
        selectedAssetValue &&
            (Boolean(selectedLibraryImage) ||
                STOCK_IMAGES.some((url) =>
                    mediaUrlsMatch(url, selectedAssetValue)
                ))
    );
    const imageSizeBadgeState = activeSettingsImage
        ? getImageSizeBadgeState(activeSettingsImage, settingsImageMeta)
        : null;
    const selectedExternalAssetValue =
        selectedAssetValue && !hasSelectedLibraryImage
            ? selectedAssetValue
            : '';
    const selectedExternalAssetName = getMediaDisplayName(
        selectedExternalAssetValue,
        selectedAssetType
    );

    const handleImageClick = (url) => {
        const normalizedUrl = String(url || '').trim();
        const isAlreadySelected =
            normalizedUrl.length > 0 &&
            mediaUrlsMatch(selectedAssetValue, normalizedUrl);
        if (isAlreadySelected) {
            setInputValue('');
            setPreviewValue('');
            applyMediaValue('');
            return;
        }

        setInputValue(url);
        setPreviewValue(url);
        applyMediaValue(url);
    };

    useEffect(() => {
        if (!activeSettingsImage) {
            setSettingsNameDraft('');
            setSettingsImageMeta(null);
            return;
        }

        setSettingsNameDraft(
            splitFilenameParts(
                String(
                    activeSettingsImage.filename ||
                        getMediaDisplayName(activeSettingsImage.url, 'image')
                ).trim()
            ).name
        );
        setSettingsAltDraft(
            readSiteImageAltText(activeSettingsImage) ||
                (mediaUrlsMatch(activeSettingsImage.url, selectedAssetValue)
                    ? String(inlineAltValue || '')
                    : '')
        );

        const fallbackWidth = Number(
            activeSettingsImage.width || activeSettingsImage.pixel_width || 0
        );
        const fallbackHeight = Number(
            activeSettingsImage.height || activeSettingsImage.pixel_height || 0
        );
        const fallbackSizeBytes = Number(activeSettingsImage.size_bytes || 0);

        if (fallbackWidth > 0 && fallbackHeight > 0 && fallbackSizeBytes > 0) {
            setSettingsImageMeta({
                width: fallbackWidth,
                height: fallbackHeight,
                sizeBytes: fallbackSizeBytes,
            });
            return;
        }

        if (fallbackWidth > 0 && fallbackHeight > 0) {
            setSettingsImageMeta((current) => ({
                ...(current || {}),
                width: fallbackWidth,
                height: fallbackHeight,
                ...(fallbackSizeBytes > 0
                    ? { sizeBytes: fallbackSizeBytes }
                    : {}),
            }));
        }

        if (fallbackSizeBytes > 0) {
            setSettingsImageMeta((current) => ({
                ...(current || {}),
                sizeBytes: fallbackSizeBytes,
            }));
            return;
        }

        let cancelled = false;
        const img = new Image();
        img.onload = () => {
            if (cancelled) return;
            setSettingsImageMeta({
                width: img.naturalWidth || 0,
                height: img.naturalHeight || 0,
            });
        };
        img.onerror = () => {
            if (cancelled) return;
            setSettingsImageMeta(null);
        };
        img.src = activeSettingsImage.url;

        const resolveSize = async () => {
            try {
                const response = await fetch(
                    `/api/image-metadata?url=${encodeURIComponent(
                        activeSettingsImage.url
                    )}`
                );
                if (cancelled || !response.ok) return;
                const data = await response.json().catch(() => null);
                const sizeBytes = Number(data?.sizeBytes || 0);
                if (Number.isFinite(sizeBytes) && sizeBytes > 0) {
                    setSettingsImageMeta((current) => ({
                        ...(current || {}),
                        sizeBytes,
                    }));
                }
            } catch {
                if (cancelled) return;
            }
        };

        resolveSize();

        return () => {
            cancelled = true;
            img.onload = null;
            img.onerror = null;
        };
    }, [activeSettingsImage, inlineAltValue, selectedAssetValue]);

    const handleSaveImageSettings = useCallback(async () => {
        if (!activeSettingsImage?.image_id) return;

        const currentFilename = String(
            activeSettingsImage.filename ||
                getMediaDisplayName(activeSettingsImage.url, 'image')
        ).trim();
        const currentParts = splitFilenameParts(currentFilename);
        const trimmedName = String(settingsNameDraft || '').trim();
        const nextFilename = trimmedName
            ? buildFilenameFromParts(trimmedName, currentParts.extension)
            : currentFilename;
        const companyId =
            editor.global?.data?.company_id || editor.site?.data?.company_id;

        try {
            setIsSavingImageSettings(true);

            let updatedImage = null;
            if (nextFilename && nextFilename !== currentFilename) {
                if (!companyId) {
                    throw new Error('Missing company ID.');
                }

                updatedImage = await renameImageAsset({
                    companyId,
                    currentFilename,
                    currentUrl: activeSettingsImage.url,
                    nextFilename,
                });
            }

            const mergedData = {
                ...(nextFilename && nextFilename !== currentFilename
                    ? {
                          filename:
                              updatedImage?.filename ||
                              nextFilename ||
                              currentFilename,
                          url: updatedImage?.url || activeSettingsImage.url,
                      }
                    : {}),
                alt_text: String(settingsAltDraft ?? ''),
            };

            editor.site.updateImage?.({
                image_id: activeSettingsImage.image_id,
                data: mergedData,
            });

            if (mediaUrlsMatch(activeSettingsImage?.url, selectedAssetValue)) {
                onInlineAltChange?.(settingsAltDraft);
            }

            setActiveSettingsImageId(null);
        } catch (error) {
            notifications.push({
                title: 'Failed to save image settings',
                variant: 'error',
                error,
            });
        } finally {
            setIsSavingImageSettings(false);
        }
    }, [
        activeSettingsImage,
        editor.site,
        editor.global,
        onInlineAltChange,
        selectedAssetValue,
        settingsAltDraft,
        settingsNameDraft,
        notifications,
    ]);

    const renderPreview = () => {
        const baseScrollClasses =
            'inline-flex self-start border border-neutral-200 rounded-[6px] overflow-hidden shadow';
        const scrollStyle = {
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'auto',
        };

        if (!previewValue) {
            return (
                <div
                    className={`inline-flex h-60 max-w-3xl items-center justify-center ${baseScrollClasses} border-dashed border-neutral-300 bg-white px-4 text-xs text-neutral-400`}
                    style={scrollStyle}
                >
                    Select an image or paste a link below to preview it here.
                </div>
            );
        }

        if (previewType === 'video') {
            return (
                <div
                    className={`relative w-full max-w-3xl ${baseScrollClasses} bg-black/70`}
                    style={{ ...scrollStyle, aspectRatio: '16 / 9' }}
                >
                    {isFacebookVideoUrl(previewValue) ? (
                        <iframe
                            title="Facebook video preview"
                            src={getFacebookEmbedUrl(previewValue)}
                            className="absolute left-0 top-0 h-full w-full"
                            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    ) : (
                        <ReactPlayer
                            url={previewValue}
                            controls
                            width="100%"
                            height="100%"
                        />
                    )}
                </div>
            );
        }

        if (previewType === 'image') {
            return (
                <div
                    className={`inline-flex max-w-3xl items-center justify-start ${baseScrollClasses} bg-white`}
                >
                    <ImageThumbnail
                        value={previewValue}
                        alt="Selected preview"
                        className="block h-auto max-h-60 w-full max-w-full select-none object-contain"
                        svgClassName="block h-auto max-h-60 w-full max-w-full select-none [&>svg]:h-auto [&>svg]:max-h-60 [&>svg]:w-full [&>svg]:max-w-full"
                    />
                </div>
            );
        }

        if (previewType === 'embed') {
            return (
                <div
                    className={`inline-flex min-h-[5rem] w-full items-stretch justify-start ${baseScrollClasses} overflow-auto bg-white`}
                    style={{ ...scrollStyle, maxHeight: '18rem' }}
                >
                    <div
                        className="min-h-full min-w-full text-[11px] text-neutral-600"
                        dangerouslySetInnerHTML={{ __html: previewValue }}
                    />
                </div>
            );
        }

        return (
            <div
                className={`inline-flex h-24 max-w-3xl items-center justify-center ${baseScrollClasses} border-neutral-200 bg-white text-xs text-neutral-500`}
                style={scrollStyle}
            >
                Preview not available for this link.
            </div>
        );
    };

    /* ----------------------------- Render ----------------------------- */
    return (
        <div
            className={
                inline
                    ? 'flex flex-col gap-2.5'
                    : 'absolute inset-0 flex h-full flex-col'
            }
        >
            {/* TOP SECTION: Preview + Gallery (shared scroll when space is tight) */}
            <div className={inline ? '' : 'flex-1 overflow-hidden pt-4'}>
                <div
                    className={
                        inline
                            ? 'flex flex-col gap-2.5'
                            : 'flex h-full min-h-0 flex-col gap-4 overflow-y-auto'
                    }
                >
                    {!hidePreview ? (
                        <div className="shrink-0">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-neutral-700">
                                    Preview
                                </span>
                            </div>
                            {renderPreview()}
                        </div>
                    ) : null}

                    <div
                        className={`min-h-[12rem] rounded-xl p-[2px] transition-colors ${
                            inline
                                ? 'w-full max-w-[19rem] self-start'
                                : 'flex-1'
                        } ${
                            !isExternalFocused && previewValue
                                ? 'bg-gradient-to-br from-primary-600 to-secondary-600'
                                : 'bg-neutral-200'
                        }`}
                    >
                        <div
                            className={`min-h-[10rem] rounded-[10px] ${
                                activeSettingsImage
                                    ? 'overflow-hidden bg-neutral-100'
                                    : 'bg-white'
                            } ${
                                inline
                                    ? 'flex max-h-[20rem] flex-col'
                                    : `h-full overflow-y-auto ${
                                          activeSettingsImage ? '' : 'p-3'
                                      }`
                            }`}
                        >
                            <div
                                className={
                                    activeSettingsImage
                                        ? inline
                                            ? 'min-h-0 flex-1 overflow-y-auto'
                                            : ''
                                        : inline
                                        ? 'min-h-0 flex-1 overflow-y-auto p-3'
                                        : ''
                                }
                            >
                                {activeSettingsImage ? (
                                    <div className="min-h-full rounded-[10px] bg-neutral-100 p-3">
                                        <div className="flex flex-col gap-3">
                                            <div className="overflow-hidden rounded border border-neutral-200 bg-neutral-50">
                                                <div className="relative flex h-40 w-full items-center justify-center overflow-hidden bg-neutral-100">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setActiveSettingsImageId(
                                                                null
                                                            )
                                                        }
                                                        className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/45 text-white shadow-sm transition hover:bg-black/65"
                                                        title="Close image settings"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                    <ImageThumbnail
                                                        value={
                                                            activeSettingsImage.url
                                                        }
                                                        alt={
                                                            activeSettingsImage.filename ||
                                                            'Selected asset'
                                                        }
                                                        className="block h-full w-full object-contain"
                                                        svgClassName="flex h-full w-full items-center justify-center overflow-hidden [&>svg]:h-full [&>svg]:max-h-full [&>svg]:w-full [&>svg]:max-w-full"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1.5 text-[10px] leading-4 text-neutral-600">
                                                <span className="inline-flex items-center rounded-md bg-neutral-600/90 px-1.5 py-0.5 font-medium text-white">
                                                    {formatImageFileType(
                                                        activeSettingsImage
                                                    )}
                                                </span>
                                                <span className="inline-flex items-center rounded-md bg-neutral-600/90 px-1.5 py-0.5 font-medium text-white">
                                                    {formatImageDimensions(
                                                        activeSettingsImage,
                                                        settingsImageMeta
                                                    )}
                                                </span>
                                                <span
                                                    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium ${imageSizeBadgeState?.className}`}
                                                    title={
                                                        imageSizeBadgeState?.title
                                                    }
                                                >
                                                    <span>
                                                        {imageSizeBadgeState?.label ||
                                                            formatImageFileSize(
                                                                activeSettingsImage,
                                                                settingsImageMeta
                                                            )}
                                                    </span>
                                                    {imageSizeBadgeState?.showWarning ? (
                                                        <span
                                                            aria-hidden="true"
                                                            className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-[10px] leading-none text-white"
                                                        >
                                                            !
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold leading-5 text-primary-700">
                                                    File Name
                                                </label>
                                                <GradientField className="mt-1">
                                                    <input
                                                        type="text"
                                                        value={
                                                            settingsNameDraft
                                                        }
                                                        onChange={(event) =>
                                                            setSettingsNameDraft(
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                        className="block h-8 w-full rounded-[10px] border-0 bg-white px-3 text-[11px] text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    />
                                                </GradientField>
                                            </div>
                                            {typeof onInlineAltChange ===
                                            'function' ? (
                                                <div>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <label className="block text-[11px] font-semibold leading-5 text-primary-700">
                                                            Alt Text
                                                        </label>
                                                        <button
                                                            type="button"
                                                            disabled
                                                            className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-neutral-200 bg-neutral-100 px-2 text-[10px] font-semibold text-neutral-400"
                                                        >
                                                            <SparklesIcon className="h-3 w-3" />
                                                            <span>
                                                                Generate
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <GradientField className="mt-1">
                                                        <textarea
                                                            value={
                                                                settingsAltDraft
                                                            }
                                                            onChange={(event) =>
                                                                setSettingsAltDraft(
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                            rows={3}
                                                            className="block min-h-[72px] w-full resize-y rounded-[10px] border-0 bg-white px-3 py-2 text-[11px] leading-5 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                        />
                                                    </GradientField>
                                                </div>
                                            ) : null}
                                            <div className="flex justify-end pt-1">
                                                <button
                                                    type="button"
                                                    onClick={
                                                        handleSaveImageSettings
                                                    }
                                                    disabled={
                                                        isSavingImageSettings
                                                    }
                                                    className="inline-flex h-8 items-center justify-center rounded-full bg-primary-600 px-3 text-xs font-semibold text-white transition hover:bg-primary-500 disabled:cursor-not-allowed disabled:bg-neutral-400"
                                                >
                                                    {isSavingImageSettings
                                                        ? 'Saving...'
                                                        : 'Save'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap items-start gap-4">
                                        {selectedExternalAssetValue ? (
                                            <div className="flex w-20 flex-col items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleImageClick(
                                                            selectedExternalAssetValue
                                                        )
                                                    }
                                                    className="group flex w-20 flex-col items-center gap-1 text-left"
                                                >
                                                    <div className="relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-neutral-200/50 shadow-sm ring-2 ring-primary-400 transition-all duration-150">
                                                        {selectedAssetType ===
                                                        'image' ? (
                                                            <ImageThumbnail
                                                                value={
                                                                    selectedExternalAssetValue
                                                                }
                                                                alt={
                                                                    selectedExternalAssetName
                                                                }
                                                                className="block h-full w-full object-contain"
                                                                svgClassName="flex h-full w-full items-center justify-center overflow-hidden [&>svg]:h-full [&>svg]:max-h-full [&>svg]:w-full [&>svg]:max-w-full"
                                                            />
                                                        ) : selectedAssetType ===
                                                          'video' ? (
                                                            <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-900 text-white">
                                                                <PlayIcon className="mb-1 h-6 w-6" />
                                                                <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                                    Video
                                                                </span>
                                                            </div>
                                                        ) : selectedAssetType ===
                                                          'embed' ? (
                                                            <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-900 text-white">
                                                                <CodeBracketIcon className="mb-1 h-6 w-6" />
                                                                <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                                    Embed
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-100 text-neutral-500">
                                                                <PhotoIcon className="mb-1 h-6 w-6" />
                                                                <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                                    External
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div
                                                        className="max-w-[5rem] truncate text-center text-[9px] leading-4 text-primary-700"
                                                        title={
                                                            selectedExternalAssetName
                                                        }
                                                    >
                                                        {
                                                            selectedExternalAssetName
                                                        }
                                                    </div>
                                                </button>
                                            </div>
                                        ) : null}
                                        {isImagesLoading && (
                                            <div className="w-full py-6 text-center text-xs text-neutral-400">
                                                <div className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
                                                Loading images...
                                            </div>
                                        )}
                                        {orderedImages?.map((image) => {
                                            const displayName =
                                                image?.filename ||
                                                image?.url
                                                    ?.split('/')
                                                    .pop()
                                                    ?.split('?')[0] ||
                                                'Untitled';
                                            const isSelected = mediaUrlsMatch(
                                                selectedAssetValue,
                                                image.url
                                            );
                                            return (
                                                <div
                                                    key={image.image_id}
                                                    className="flex w-20 flex-col items-center gap-1"
                                                >
                                                    <div
                                                        className={`group relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-neutral-200/50 shadow-sm transition-all duration-150 ${
                                                            isSelected
                                                                ? 'ring-2 ring-primary-400'
                                                                : 'hover:ring-2 hover:ring-primary-300'
                                                        }`}
                                                        style={{
                                                            aspectRatio:
                                                                '4 / 3',
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="pointer-events-auto absolute right-6 top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900/80 text-white opacity-0 shadow transition-opacity duration-150 hover:bg-neutral-900 group-hover:opacity-100"
                                                            title="Image settings"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveSettingsImageId(
                                                                    image.image_id
                                                                );
                                                            }}
                                                        >
                                                            <Cog6ToothIcon className="h-2.5 w-2.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="pointer-events-auto absolute right-1 top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900/80 text-white opacity-0 shadow transition-opacity duration-150 hover:bg-neutral-900 group-hover:opacity-100"
                                                            title="Delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                editor.site.deleteImage?.(
                                                                    {
                                                                        image_id:
                                                                            image.image_id,
                                                                    }
                                                                );
                                                            }}
                                                        >
                                                            <i className="fas fa-xmark text-[8px] leading-none" />
                                                        </button>
                                                        <div className="absolute inset-1">
                                                            <img
                                                                src={image.url}
                                                                alt={
                                                                    displayName
                                                                }
                                                                className="block h-full w-full cursor-pointer object-contain"
                                                                draggable={
                                                                    false
                                                                }
                                                                title="Select"
                                                                onClick={() =>
                                                                    handleImageClick(
                                                                        image.url
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="max-w-[5rem] truncate text-center text-[9px] leading-4 text-neutral-600"
                                                        title={displayName}
                                                    >
                                                        {displayName}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {showStockImages &&
                                            STOCK_IMAGES.map((url) => {
                                                const isSelected =
                                                    mediaUrlsMatch(
                                                        selectedAssetValue,
                                                        url
                                                    );
                                                return (
                                                    <div
                                                        key={url}
                                                        className={`relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded border bg-white shadow-sm transition ${
                                                            isSelected
                                                                ? 'border-primary-400 ring-2 ring-primary-300'
                                                                : 'border-neutral-200 hover:border-primary-400'
                                                        }`}
                                                        style={{
                                                            aspectRatio:
                                                                '4 / 3',
                                                        }}
                                                        onClick={() =>
                                                            handleImageClick(
                                                                url
                                                            )
                                                        }
                                                    >
                                                        <img
                                                            src={url}
                                                            alt="Stock asset"
                                                            className="block h-full w-full cursor-pointer object-contain"
                                                            draggable={false}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        {(!filteredImages ||
                                            filteredImages.length === 0) && (
                                            <div className="w-full py-8 text-center text-xs text-neutral-500">
                                                <div className="mb-3">
                                                    No images uploaded yet.
                                                </div>
                                                <div className="flex flex-wrap justify-center gap-3">
                                                    {showStockImages
                                                        ? STOCK_IMAGES.map(
                                                              (url) => (
                                                                  <button
                                                                      key={url}
                                                                      type="button"
                                                                      onClick={() =>
                                                                          handleImageClick(
                                                                              url
                                                                          )
                                                                      }
                                                                      className="h-16 w-20 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm transition hover:border-primary-400"
                                                                  >
                                                                      <img
                                                                          src={
                                                                              url
                                                                          }
                                                                          alt="Stock asset"
                                                                          className="h-full w-full object-contain"
                                                                          draggable={
                                                                              false
                                                                          }
                                                                      />
                                                                  </button>
                                                              )
                                                          )
                                                        : null}
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex w-full flex-col gap-1">
                                            <GradientField className="w-full">
                                                <textarea
                                                    rows={3}
                                                    value={inputValue}
                                                    onChange={(event) =>
                                                        handleInputChange(
                                                            event.target.value
                                                        )
                                                    }
                                                    onKeyDown={(event) => {
                                                        if (
                                                            event.key ===
                                                                'Enter' &&
                                                            !event.shiftKey &&
                                                            isValid &&
                                                            !isVideoAndDisabled
                                                        ) {
                                                            event.preventDefault();
                                                            handleApply();
                                                        }
                                                    }}
                                                    onFocus={
                                                        handleExternalInputFocus
                                                    }
                                                    onBlur={() =>
                                                        setIsExternalFocused(
                                                            false
                                                        )
                                                    }
                                                    className="block min-h-[5rem] w-full resize-y rounded-[10px] border-0 bg-white px-3 py-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                                    placeholder={
                                                        inputPlaceholder
                                                    }
                                                />
                                            </GradientField>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="truncate text-[9px] leading-4 text-primary-700">
                                                    {inputHelperText}
                                                </div>
                                                {inputValue ? (
                                                    <span
                                                        className={`shrink-0 text-[8px] font-bold uppercase tracking-[0.12em] ${
                                                            isVideoAndDisabled
                                                                ? 'text-red-500'
                                                                : 'text-primary-600'
                                                        }`}
                                                    >
                                                        {isVideoAndDisabled
                                                            ? 'Video Off'
                                                            : detectedType ===
                                                              'unknown'
                                                            ? 'Unknown'
                                                            : detectedType}
                                                    </span>
                                                ) : null}
                                            </div>
                                            {showBackgroundVideoWarning &&
                                            isFacebookVideoUrl(inputValue) ? (
                                                <p className="text-[9px] leading-4 text-amber-700">
                                                    Facebook videos and reels
                                                    cannot autoplay as
                                                    background videos.
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!inline ? (
                <div className="mx-[-1rem] shrink-0 bg-gradient-to-r from-primary-700 to-secondary-700 px-6 py-3">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="inline-flex min-w-[6rem] items-center justify-center rounded-4xl border border-white bg-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={handleApply}
                            disabled={!isValid || isVideoAndDisabled}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
